import React, { useEffect, useRef, useState } from 'react';
import { PageAtmosphere, PageAtmosphereVariant } from './PageAtmosphere';
import { useMotionPreference } from '../hooks/useMotionPreference';
import '../styles/kinetic-object.css';

interface KineticObjectProps {
  variant: PageAtmosphereVariant;
  className?: string;
  paused?: boolean;
  label?: string;
}

/** Preserves the original orbital artwork, adding a bounded interactive depth layer. */
export const KineticObject: React.FC<KineticObjectProps> = ({ variant, className = '', paused = false, label = 'orbital sculpture' }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>();
  const [reframed, setReframed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(true);
  const { motionDisabled } = useMotionPreference();
  const still = paused || motionDisabled || !visible || !documentVisible;
  const motionStyle = still ? { transition: 'none' } : undefined;

  const reset = () => {
    if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    frameRef.current = undefined;
    const root = rootRef.current;
    root?.style.setProperty('--object-x', '0deg');
    root?.style.setProperty('--object-y', '0deg');
    root?.style.setProperty('--light-x', '50%');
    root?.style.setProperty('--light-y', '50%');
  };

  useEffect(() => {
    const updateVisibility = () => setDocumentVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    const root = rootRef.current;
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: '40px' }) : null;
    if (root && observer) observer.observe(root);
    else setVisible(true);
    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      observer?.disconnect();
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  useEffect(() => {
    if (still) reset();
    rootRef.current?.querySelectorAll<SVGSVGElement>('svg').forEach(svg => {
      if (still) svg.pauseAnimations?.();
      else svg.unpauseAnimations?.();
    });
  }, [still, variant]);

  const move = (event: React.PointerEvent<HTMLDivElement>) => {
    if (still || event.pointerType !== 'mouse') return;
    const { clientX, clientY } = event;
    if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined;
      const root = rootRef.current;
      if (!root || document.hidden) return;
      const bounds = root.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, ((clientX - bounds.left) / bounds.width - .5) * 2));
      const y = Math.max(-1, Math.min(1, ((clientY - bounds.top) / bounds.height - .5) * 2));
      root.style.setProperty('--object-x', `${x * 10}deg`);
      root.style.setProperty('--object-y', `${y * -8}deg`);
      root.style.setProperty('--light-x', `${50 + x * 25}%`);
      root.style.setProperty('--light-y', `${50 + y * 25}%`);
    });
  };

  return (
    <div ref={rootRef} className={`cx-kinetic ${className}${still ? ' is-still' : ''}${reframed ? ' is-reframed' : ''}`} onPointerMove={move} onPointerLeave={reset}>
      <button type="button" className="cx-kinetic-control" aria-label={`Change perspective of ${label}`} aria-pressed={reframed} onClick={() => setReframed(value => !value)} onBlur={reset}>
        <span className="cx-kinetic-light" style={motionStyle} aria-hidden="true" />
        <span className="cx-kinetic-tilt" style={motionStyle}><span className="cx-kinetic-view" style={motionStyle}><PageAtmosphere variant={variant} /></span></span>
        <span className="cx-kinetic-hint" style={motionStyle} aria-hidden="true">Change perspective <span>↗</span></span>
      </button>
    </div>
  );
};
